/** Quem tem aberta a sessão partilhada agora.
 *
 *  O canal é o do BCF ao vivo, com `projeto=sessao:<id da partilha>`
 *  (backend/modules/bcf/ao_vivo_router.py): autenticado pela sessão, só
 *  entra quem tem acesso à partilha, e só traz presença. Uma entrada por
 *  janela; aqui junta-se por pessoa.
 *
 *  Pedido do Rui (15/10/2026), depois da primeira sessão com o Vítor:
 *  "não vejo forma de saber se estou ou não em sessão partilhada, quem
 *  está online nessa sessão". */
import type { EstadoDoCanal, PresenteAoVivo } from '@modules/bcf/lib/bcfCanalAoVivo'

export interface PessoaOnline {
  utilizador: string
  nome: string
  janelas: number
}

/** O canal de presença de uma partilha de sessão. */
export function projetoDoCanalDaSessao(partilhaId: string): string {
  return `sessao:${partilhaId}`
}

/** As outras pessoas (sem mim), uma vez cada, por ordem de chegada. */
export function outrasPessoas(participantes: readonly PresenteAoVivo[], eu: string | null): PessoaOnline[] {
  const porPessoa = new Map<string, PessoaOnline>()
  for (const p of participantes) {
    if (p.utilizador === eu) continue
    const ja = porPessoa.get(p.utilizador)
    if (ja) ja.janelas += 1
    else porPessoa.set(p.utilizador, { utilizador: p.utilizador, nome: p.nome, janelas: 1 })
  }
  return [...porPessoa.values()]
}

/** "com Vítor", "com Vítor e João", "com Vítor, João e Ana", "com 5
 *  pessoas", ou "só você". */
export function quemMaisEsta(outras: readonly PessoaOnline[]): string {
  const nomes = outras.map((p) => primeiroNome(p.nome))
  if (nomes.length === 0) return 'só você'
  if (nomes.length > 3) return `com ${nomes.length} pessoas`
  if (nomes.length === 1) return `com ${nomes[0]}`
  return `com ${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}`
}

/** O texto completo do indicador, para a dica ao passar o rato. */
export function dicaDoIndicador(
  nomeDaSessao: string,
  outras: readonly PessoaOnline[],
  estado: EstadoDoCanal,
): string {
  const cabeca = `Sessão partilhada «${nomeDaSessao}».`
  if (estado === 'recusado') return `${cabeca} Sem acesso à presença desta sessão.`
  if (estado !== 'ligado') return `${cabeca} A ligar para saber quem está online…`
  if (outras.length === 0) return `${cabeca} Online agora: só você. Clique para gerir com quem está partilhada.`
  const lista = outras
    .map((p) => (p.janelas > 1 ? `${p.nome} (${p.janelas} janelas)` : p.nome))
    .join(', ')
  return `${cabeca} Online agora, além de si: ${lista}. Clique para gerir com quem está partilhada.`
}

/** O nome curto para o chip: a primeira palavra, ou a parte antes do @
 *  de um email (o servidor manda o email quando a conta não tem nome). */
export function primeiroNome(nome: string): string {
  const n = nome.trim()
  if (n.includes('@')) return n.split('@')[0] || n
  return n.split(/\s+/)[0] || n
}
